<template>
    <div class="diff-page">
        <div class="main_body">
            <!-- 标题 -->
            <div class="top_label">
                <span class="lable_name">成绩对比图</span>
            </div>

            <!-- 配置面板 -->
            <div class="config_box">
                <div class="config_section">
                    <div class="config_section_title">
                        <span>分数分段设置</span>
                        <el-button size="mini" type="primary" plain @click="addSegment">
                            <i class="el-icon-plus"></i> 添加分段
                        </el-button>
                    </div>
                    <div class="segment_list">
                        <div class="segment_header">
                            <span class="col-label">分段名称</span>
                            <span class="col-min">最低分</span>
                            <span class="col-max">最高分</span>
                            <span class="col-action">操作</span>
                        </div>
                        <div class="segment_item" v-for="(seg, idx) in segments" :key="idx">
                            <el-input v-model="seg.label" size="small" placeholder="如 0-59分" class="col-label"></el-input>
                            <el-input-number v-model="seg.min" size="small" :min="0" :max="150" controls-position="right" class="col-min"></el-input-number>
                            <el-input-number v-model="seg.max" size="small" :min="0" :max="150" controls-position="right" class="col-max"></el-input-number>
                            <el-button type="text" size="small" style="color:#f56c6c" @click="removeSegment(idx)" :disabled="segments.length <= 2">
                                <i class="el-icon-delete"></i>
                            </el-button>
                        </div>
                    </div>
                </div>

                <div class="config_section">
                    <div class="config_section_title">
                        <span>期数数据（按期数对比）</span>
                        <el-button size="mini" type="primary" plain @click="addPeriod">
                            <i class="el-icon-plus"></i> 添加期数
                        </el-button>
                    </div>
                    <div class="period_list">
                        <!-- 格式提示 -->
                        <div class="period_format_tip">
                            <i class="el-icon-info"></i>
                            <span>期数名称示例：25年上学期一阶段 / 25年下学期二阶段。最后一个分段根据「总人数 - 前面各分段之和」自动计算填充。</span>
                        </div>

                        <!-- 表头行 -->
                        <div class="period_header_row">
                            <span class="col-name">期数名称</span>
                            <span class="col-total">总人数</span>
                            <span
                                class="col-score"
                                v-for="(seg, idx) in segments"
                                :key="'h-' + idx"
                                :class="{ 'col-auto': idx === segments.length - 1 }"
                            >
                                {{ seg.label || '分段' + (idx + 1) }}
                                <i v-if="idx === segments.length - 1" class="el-icon-magic-stick col-auto-tip" title="自动计算"></i>
                            </span>
                            <span class="col-action">操作</span>
                        </div>

                        <!-- 数据行 -->
                        <div class="period_row" v-for="(period, pIdx) in periods" :key="pIdx">
                            <el-input
                                v-model="period.name"
                                size="small"
                                placeholder="如 25年上学期一阶段"
                                class="col-name"
                            ></el-input>
                            <el-input-number
                                v-model="period.total"
                                size="small"
                                :min="0"
                                controls-position="right"
                                class="col-total"
                                @change="autoFillLast(pIdx)"
                            ></el-input-number>
                            <!-- 可编辑的分段（除最后一个） -->
                            <el-input-number
                                v-for="(seg, sIdx) in segments"
                                :key="sIdx"
                                v-if="sIdx < segments.length - 1"
                                v-model="period.values[sIdx]"
                                size="small"
                                :min="0"
                                controls-position="right"
                                class="col-score"
                                @change="autoFillLast(pIdx)"
                            ></el-input-number>
                            <!-- 最后一个分段：自动计算 -->
                            <div class="col-score col-auto-display">
                                {{ getAutoValue(period) }}
                            </div>
                            <el-button type="text" size="small" style="color:#f56c6c" @click="removePeriod(pIdx)" :disabled="periods.length <= 1">
                                <i class="el-icon-delete"></i>
                            </el-button>
                        </div>
                    </div>
                </div>

                <!-- 操作按钮 -->
                <div class="action_box">
                    <el-button type="primary" @click="generateChart" :disabled="!canGenerate">
                        <i class="el-icon-data-analysis"></i> 生成对比图
                    </el-button>
                    <el-button @click="resetAll">
                        <i class="el-icon-refresh"></i> 重置
                    </el-button>
                </div>
            </div>

            <!-- 图表区域 -->
            <div class="chart_box" v-if="chartVisible">
                <div class="chart_box_title">
                    <span>成绩分段对比图</span>
                    <el-button size="mini" text @click="downloadChart">
                        <i class="el-icon-download"></i> 下载图表
                    </el-button>
                </div>
                <div ref="chartRef" class="chart_container"></div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
    name: 'Diff',
    data() {
        return {
            chart: null,
            chartVisible: false,
            // 分段配置
            segments: [
                { label: '0-59分', min: 0, max: 59 },
                { label: '60-69分', min: 60, max: 69 },
                { label: '70-79分', min: 70, max: 79 },
                { label: '80-89分', min: 80, max: 89 },
                { label: '90-100分', min: 90, max: 100 }
            ],
            // 期数数据（total = 总人数，values = 各分段人数，最后一个由总人数自动算出）
            periods: [
                { name: '', total: 0, values: [0, 0, 0, 0] }
            ],
            // 颜色方案
            colorPalette: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#7C3AED', '#06B6D4', '#F97316']
        }
    },
    computed: {
        // 是否可以生成图表
        canGenerate() {
            if (this.periods.length < 1) return false
            if (this.segments.length < 2) return false
            return this.periods.every(p => p.name && p.name.trim() && p.total !== undefined && p.total !== null)
        },
        // X轴类别
        xAxisData() {
            return this.segments.map(s => s.label || `${s.min}-${s.max}分`)
        }
    },
    watch: {
        // 分段数量变化时，同步调整每期的 values 数组长度（最后一个分段不存 values，是自动算的）
        segments: {
            handler(newSegs, oldSegs) {
                if (!oldSegs) return
                // values 存的是"除最后一个分段之外"的值
                const expectLen = Math.max(0, newSegs.length - 1)
                this.periods.forEach(p => {
                    while (p.values.length < expectLen) {
                        p.values.push(0)
                    }
                    while (p.values.length > expectLen) {
                        p.values.pop()
                    }
                })
            },
            deep: true
        }
    },
    beforeDestroy() {
        this.disposeChart()
        window.removeEventListener('resize', this.handleResize)
    },
    methods: {
        /**
         * 计算最后一个分段的自动值
         * 规则：总人数 - 前面所有分段之和（负数时显示0）
         */
        getAutoValue(period) {
            if (!period || !period.total) return 0
            const sum = (period.values || []).reduce((a, b) => a + (b || 0), 0)
            const remain = period.total - sum
            return remain >= 0 ? remain : 0
        },
        /**
         * 将自动值回写到 values 最后一位（供图表使用）
         */
        getFullValues(period) {
            const autoVal = this.getAutoValue(period)
            return [...(period.values || []), autoVal]
        },
        /**
         * 总人数或前面分段变化时重新计算（UI层已通过 getAutoValue 实时显示，这里留空供扩展）
         */
        autoFillLast(/* pIdx */) {
            // 纯展示驱动，不需要额外操作
        },
        // 添加分段
        addSegment() {
            const last = this.segments[this.segments.length - 1]
            const newMin = last ? last.max + 1 : 0
            const newMax = newMin + 9
            this.segments.push({
                label: `${newMin}-${newMax}分`,
                min: newMin,
                max: newMax
            })
        },
        // 删除分段
        removeSegment(idx) {
            if (this.segments.length <= 2) return
            this.segments.splice(idx, 1)
        },
        // 添加期数
        addPeriod() {
            const newValues = Array(Math.max(0, this.segments.length - 1)).fill(0)
            this.periods.push({
                name: '',
                total: 0,
                values: newValues
            })
        },
        // 删除期数
        removePeriod(idx) {
            if (this.periods.length <= 1) return
            this.periods.splice(idx, 1)
        },
        // 重置
        resetAll() {
            this.$confirm('确定要重置所有数据吗？', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.segments = [
                    { label: '0-59分', min: 0, max: 59 },
                    { label: '60-69分', min: 60, max: 69 },
                    { label: '70-79分', min: 70, max: 79 },
                    { label: '80-89分', min: 80, max: 89 },
                    { label: '90-100分', min: 90, max: 100 }
                ]
                this.periods = [
                    { name: '', total: 0, values: [0, 0, 0, 0] }
                ]
                this.chartVisible = false
                this.disposeChart()
                this.$message.success('已重置')
            }).catch(() => {})
        },
        // 生成图表
        generateChart() {
            if (!this.canGenerate) {
                this.$message.warning('请完善期数名称和总人数')
                return
            }
            this.chartVisible = true
            this.$nextTick(() => {
                this.renderChart()
            })
        },
        // 渲染 ECharts
        renderChart() {
            if (!this.$refs.chartRef) return

            if (this.chart) {
                this.chart.dispose()
            }
            this.chart = echarts.init(this.$refs.chartRef)

            const series = this.periods.map((period, idx) => ({
                name: period.name,
                type: 'bar',
                barGap: '10%',
                barCategoryGap: '20%',
                itemStyle: {
                    borderRadius: [4, 4, 0, 0],
                    color: this.colorPalette[idx % this.colorPalette.length]
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.3)'
                    }
                },
                label: {
                    show: true,
                    position: 'top',
                    color: '#606266',
                    fontSize: 12
                },
                data: this.getFullValues(period).map((v, i) => ({
                    value: v,
                    periodName: period.name,
                    segLabel: this.xAxisData[i]
                }))
            }))

            const option = {
                title: {
                    text: '成绩分段对比图',
                    left: 'center',
                    top: 10,
                    textStyle: {
                        fontSize: 16,
                        fontWeight: 600,
                        color: '#303133'
                    }
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    },
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderColor: '#e4e7ed',
                    borderWidth: 1,
                    textStyle: {
                        color: '#303133'
                    },
                    formatter: (params) => {
                        let result = `<div style="font-weight:600;margin-bottom:6px;">${params[0].axisValue}</div>`
                        params.forEach(p => {
                            result += `<div style="display:flex;align-items:center;gap:6px;margin:3px 0;">
                                <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${p.color};"></span>
                                <span>${p.seriesName}：</span>
                                <span style="font-weight:600;color:${p.color};">${p.value} 人</span>
                            </div>`
                        })
                        return result
                    }
                },
                legend: {
                    data: this.periods.map(p => p.name),
                    top: 40,
                    icon: 'roundRect',
                    itemWidth: 14,
                    itemHeight: 10,
                    textStyle: {
                        color: '#606266'
                    }
                },
                grid: {
                    left: 60,
                    right: 30,
                    top: 90,
                    bottom: 50,
                    containLabel: true
                },
                xAxis: {
                    type: 'category',
                    data: this.xAxisData,
                    axisLine: {
                        lineStyle: { color: '#dcdfe6' }
                    },
                    axisTick: { show: false },
                    axisLabel: {
                        color: '#606266',
                        fontSize: 12
                    }
                },
                yAxis: {
                    type: 'value',
                    name: '人数（人）',
                    nameTextStyle: {
                        color: '#909399',
                        fontSize: 12
                    },
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: {
                        color: '#909399',
                        fontSize: 12
                    },
                    splitLine: {
                        lineStyle: {
                            color: '#f0f2f5',
                            type: 'dashed'
                        }
                    }
                },
                series: series
            }

            this.chart.setOption(option, true)
            window.addEventListener('resize', this.handleResize)
        },
        handleResize() {
            if (this.chart) {
                this.chart.resize()
            }
        },
        disposeChart() {
            if (this.chart) {
                this.chart.dispose()
                this.chart = null
            }
        },
        // 下载图表
        downloadChart() {
            if (!this.chart) return
            const url = this.chart.getDataURL({
                type: 'png',
                pixelRatio: 2,
                backgroundColor: '#fff'
            })
            const link = document.createElement('a')
            link.download = `成绩对比图_${Date.now()}.png`
            link.href = url
            link.click()
        }
    }
}
</script>

<style scoped>
.diff-page {
    min-height: 100vh;
    background: #f5f7fa;
}
.main_body {
    padding: 20px;
    max-width: 1400px;
    margin: 0 auto;
}
.top_label {
    background: #fff;
    padding: 16px 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}
.lable_name {
    font-size: 18px;
    font-weight: 700;
    color: #303133;
}

/* 配置面板 */
.config_box {
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    margin-top: 16px;
}
.config_section {
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #ebeef5;
}
.config_section:last-of-type {
    border-bottom: none;
    margin-bottom: 16px;
}
.config_section_title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
}
.config_section_title > span {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
}

/* 分段列表 */
.segment_list {
    background: #fafbfc;
    border-radius: 6px;
    padding: 12px;
}
.segment_header,
.segment_item {
    display: flex;
    align-items: center;
    gap: 12px;
}
.segment_header {
    font-size: 12px;
    color: #909399;
    margin-bottom: 8px;
    padding: 0 4px;
}
.segment_item {
    margin-bottom: 8px;
}
.segment_item:last-child {
    margin-bottom: 0;
}
.col-label {
    width: 160px;
}
.col-min {
    width: 130px;
}
.col-max {
    width: 130px;
}
.col-action {
    width: 60px;
    text-align: center;
}

/* 期数列表 */
.period_list {
    background: #fafbfc;
    border-radius: 6px;
    padding: 12px;
    overflow-x: auto;
}
.period_format_tip {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #909399;
    padding: 6px 8px;
    margin-bottom: 10px;
    background: #ecf5ff;
    border-radius: 4px;
    border: 1px solid #d9ecff;
}
.period_format_tip i {
    color: #409eff;
}
.period_header_row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: #909399;
    margin-bottom: 8px;
    padding: 0 4px;
    white-space: nowrap;
}
.period_row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    white-space: nowrap;
}
.period_row:last-child {
    margin-bottom: 0;
}
.col-name {
    width: 170px;
    flex-shrink: 0;
}
.col-total {
    width: 130px;
    flex-shrink: 0;
}
.col-score {
    width: 130px;
    flex-shrink: 0;
}
.col-auto {
    color: #409eff;
    font-weight: 500;
}
.col-auto-tip {
    margin-left: 4px;
    color: #409eff;
    font-size: 12px;
}
.col-auto-display {
    height: 32px;
    line-height: 32px;
    text-align: center;
    background: #ecf5ff;
    border: 1px solid #b3d8ff;
    border-radius: 4px;
    color: #409eff;
    font-weight: 600;
    font-size: 14px;
}
.period_header_row .col-action,
.period_row .col-action {
    width: 60px;
    text-align: center;
    flex-shrink: 0;
}

/* 操作按钮 */
.action_box {
    display: flex;
    justify-content: center;
    gap: 12px;
}
.action_box .el-button {
    padding: 10px 28px;
    font-size: 14px;
}

/* 图表区域 */
.chart_box {
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    margin-top: 16px;
}
.chart_box_title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}
.chart_box_title > span {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
}
.chart_container {
    width: 100%;
    height: 500px;
}
</style>
